import { formatDate, type Locale } from "@financialharmony/i18n"
import { TODO_TITLE_DATA_VERSION, todoTitleFault, type TodoTitleFault } from "@financialharmony/todo-title"
import { upcastPayload, type PayloadSchema, type UpcastFailure } from "@financialharmony/versioned-payload"
import { formatAmount } from "./format"

/**
 * The kinds, their facts and the version writers produce live in `@financialharmony/todo-title`. To
 * change a kind's facts incompatibly, bump the version there, add the hop to the schema's
 * `upcasters` below, and keep a golden fixture of the old shape (see the `versioned-payloads`
 * skill).
 */
export { TODO_TITLE_DATA_VERSION }

export const TODO_TITLE_SCHEMA: PayloadSchema = { current: TODO_TITLE_DATA_VERSION, upcasters: {} }

/** Why a payload showed the generic sentence; reported so a reader never fails silently. */
export type TodoTitleUnreadable = UpcastFailure | TodoTitleFault

/**
 * What a system-created todo's title is made of. The backend stores this instead of a sentence
 * (`todo_items.title_data`, FIN-1524) and each app words it here, from the `todoText` catalog
 * namespace, in the user's language. Every field but `kind` is optional because it is data from the
 * wire: a kind the app does not know, or a known one missing a field, reads as the generic sentence.
 */
export interface TodoTitleData {
  kind: string
  /** Payload version; absent means 1. */
  v?: number
  /** Bank-provided counterparty name, shown as it came. */
  vendor?: string
  /** Bank-provided description, shown as it came; used when there is no vendor. */
  description?: string
  /** A positive figure in major units. */
  amount?: number
  currency?: string
  /** `YYYY-MM-DD`. */
  date?: string
  authority?: string
  accountLabel?: string
  /** A tax account transaction type (`vat`, `employer_contributions`, …). */
  taxType?: string
}

/** The `todoText` namespace as either app's translator exposes it. */
export interface TodoTextMessages {
  (key: string, values?: Record<string, string>): string
  has(key: string): boolean
}

function uploadReceiptTitle(t: TodoTextMessages, locale: Locale, data: TodoTitleData): string | undefined {
  if (data.amount === undefined || !data.currency || !data.date || !/^\d{4}-\d{2}-\d{2}$/.test(data.date)) return undefined
  const values = {
    amount: formatAmount(data.amount, data.currency),
    // Noon UTC keeps a date-only value on its own day in every time zone.
    date: formatDate(`${data.date}T12:00:00Z`, locale, "dayMonth"),
  }
  if (data.vendor) return t("uploadReceipt.vendor", { ...values, vendor: data.vendor })
  if (data.description) return t("uploadReceipt.description", { ...values, description: data.description })
  return t("uploadReceipt.generic", values)
}

function titleFromData(t: TodoTextMessages, locale: Locale, data: TodoTitleData): string | undefined {
  switch (data.kind) {
    case "upload_receipt":
      return uploadReceiptTitle(t, locale, data)
    case "classify_document":
      return t("classifyDocument")
    case "reconnect_tax_account":
      return data.authority && data.accountLabel
        ? t("reconnectTaxAccount", { authority: data.authority, accountLabel: data.accountLabel })
        : undefined
    case "pay_payable": {
      const key = data.taxType && t.has(`payTax.types.${data.taxType}`) ? data.taxType : "other"
      return t("payTax.title", { taxType: t(`payTax.types.${key}`) })
    }
    default:
      return undefined
  }
}

/**
 * The title a todo shows: a system todo's `titleData` worded in the user's language, else a
 * consultant's own typed `title`, else a generic sentence. Never the raw data and never a key.
 */
export function todoTitleText(
  t: TodoTextMessages,
  locale: Locale,
  todo: { title?: string | null; titleData?: TodoTitleData | null },
  onUnreadable?: (reason: TodoTitleUnreadable, data: TodoTitleData) => void,
): string {
  if (todo.titleData) {
    const upcast = upcastPayload(todo.titleData, TODO_TITLE_SCHEMA)
    if (!upcast.ok) {
      onUnreadable?.(upcast.reason, todo.titleData)
    } else {
      const data = upcast.payload as unknown as TodoTitleData
      const fault = todoTitleFault(data)
      const text = fault ? undefined : titleFromData(t, locale, data)
      if (text) return text
      onUnreadable?.(fault ?? "missing_facts", todo.titleData)
    }
  }
  return todo.title ? todo.title : t("fallback")
}
